import { MutableRefObject, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { publishArticle } from '../../../../apis/article.api';
import DataLayer from '../../../../utils/data-layer';

export interface MissingField {
  field: string;
  label: string;
  description: string;
}

export interface FieldErrors {
  title?: string;
  cover?: string;
  content?: string;
}

export interface ValidationResult {
  error: string | null;
  missingFields: MissingField[];
  fieldErrors: FieldErrors;
}

interface UsePublishArticleProps {
  articlePayload: any;
  contributors: any[];
  profile: any;
  searchParams: URLSearchParams;
  editorStateRef: MutableRefObject<any>;
  textLengthRef: MutableRefObject<number>;
  convertLexicalScope: (params: { editorState: any }) => Promise<any>;
  setModal: (modalName: string) => void;
  setMissingFields: (fields: MissingField[]) => void;
  setFieldErrors: (errors: FieldErrors) => void;
  articleId: string;
}

export const usePublishArticle = ({
  articlePayload,
  contributors,
  profile,
  searchParams,
  editorStateRef,
  textLengthRef,
  convertLexicalScope,
  setModal,
  setMissingFields,
  setFieldErrors,
  articleId
}: UsePublishArticleProps) => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const validateArticle = (): ValidationResult => {
    const missingFields: MissingField[] = [];
    const fieldErrors: FieldErrors = {};

    // Check for title
    if (!articlePayload.title || articlePayload.title.trim() === '') {
      fieldErrors.title = 'This field is required';
      missingFields.push({
        field: 'title',
        label: 'Article Title',
        description: 'Give your article a compelling title'
      });
    }

    // Check for cover image
    if (!(articlePayload.coverCloudKey || articlePayload.cover)) {
      fieldErrors.cover = 'This field is required';
      missingFields.push({
        field: 'cover',
        label: 'Article Thumbnail',
        description: 'Upload an eye-catching image for your article'
      });
    }

    // Check for article content
    if (textLengthRef.current === 0) {
      fieldErrors.content = 'Write some content for your article';
      missingFields.push({
        field: 'content',
        label: 'Article Content',
        description: 'Write some content for your article'
      });
    }

    if (missingFields.length > 0) {
      // Determine which error modal to show
      const hasContentError = missingFields.some((f) => f.field === 'content');
      const hasMetadataError = missingFields.some(
        (f) => f.field === 'title' || f.field === 'cover'
      );

      // If only content is missing, show content error; otherwise show metadata error
      if (hasContentError && !hasMetadataError) {
        return { error: 'publish-content-error', missingFields, fieldErrors };
      }
      return { error: 'publish-metadata-error', missingFields, fieldErrors };
    }

    return { error: null, missingFields: [], fieldErrors: {} };
  };

  const formatContributors = (): Array<{
    id: string;
    role: string;
    equityPercentage: number;
    isManual: boolean;
  }> => {
    const formatted = contributors.map((contributor) => ({
      id: contributor.user.id,
      role: contributor.role.value,
      equityPercentage: contributor.equityPercentage || 0,
      isManual: contributor.isManual
    }));
    if (searchParams.get('type') !== 'edit') {
      formatted.push({ id: profile.id, role: 'author', equityPercentage: 0, isManual: true });
    }
    return formatted;
  };

  const handlePublish = async (onClose?: () => void) => {
    try {
      const validationResult = validateArticle();
      if (validationResult.error) {
        setMissingFields(validationResult.missingFields);
        setFieldErrors(validationResult.fieldErrors);
        setModal(validationResult.error);
        return;
      }

      setLoading(true);

      const content = await convertLexicalScope({
        editorState: editorStateRef.current
      });

      await publishArticle({
        ...articlePayload,
        articleId,
        topicIds: articlePayload?.topicIds?.map((topic: any) => topic.id) || [],
        contributors: formatContributors(),
        content,
        contentLength: textLengthRef.current
      });

      DataLayer.trackArticlePublish(articlePayload?.writingType, articleId);

      setTimeout(() => {
        navigate(`/article/manage?tab=articles`);
        if (onClose) onClose();
      }, 1000);
    } catch (error) {
      console.error('Error publishing article:', error);
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    handlePublish
  };
};
